import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    inject,
    input,
    output,
    SecurityContext,
    signal,
    viewChild
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {AuthService} from '../../security/auth.service';
import {MarkdownRendererComponent} from '../markdown-renderer/markdown-renderer.component';
import {Alerts, DialogButton} from '../../lib/alerts';
import {EditableComment} from '../cell-comment-editor/cell-comment-editor.component';
import {DynamicComponentBase} from '../../service/ng-dynamic-components.service';
import {AppFormat} from '../../lib/format';
import {AccountData} from '../../../api-client-kt/src';
import {CellComment, CheckUtils} from '../../data/app-data-checks';
import {StringUtils} from '../../lib/strings';
import {CollectionView, NotifyCollectionChangedAction, NotifyCollectionChangedEventArgs} from '@mescius/wijmo';
import {AccessControlService} from '../../security/access-control.service';
import {SegmentWithPermissions} from '../../data/portfolio-data-context.service';

export class AccountDataNotifyCollectionChangedEventArgs extends NotifyCollectionChangedEventArgs {
    constructor(action: NotifyCollectionChangedAction, item?: AccountData | undefined, field?: string | undefined) {
        super(action, item);
    }
}

/**
 * AccountCommentsEditorComponent is responsible for managing and rendering
 * comments for a given account and field attribute. It provides functionality
 * to add, update, delete, and render comments in markdown format. The component
 * is integrated with external services for authentication and sanitization.
 *
 * This component extends `DynamicComponentBase` and implements `AfterViewInit` to
 * handle lifecycle methods for initializing the component and auto-focusing text fields.
 *
 * Primary Features:
 * - Loading comments for a specific account and field.
 * - Adding, updating, and deleting comments.
 * - Rendering markdown content within the component.
 * - Ensuring secure sanitization of rendered markdown.
 * - Supporting user authentication and metadata operations.
 * - Handling dynamic focus and text area height adjustments.
 *
 * Inputs:
 * - `account` (AccountData): The account data containing metadata and comments.
 * - `field` (string): The name of the field with which the comments are associated.
 * - `collectionView` (CollectionView<AccountData>): The collection view used to listen
 *    for comment updates.
 * - `autofocus` (boolean | undefined): Determines whether the comment text area
 *    should be auto-focused on component initialization.
 *
 * Outputs:
 * - `onCommentsUpdated` (AccountData): Emits the updated account data whenever the
 *    comments are modified.
 *
 */
@Component({
    selector: 'app-account-comments-editor',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [],
    templateUrl: './account-comments-editor.component.html',
    styleUrl: './account-comments-editor.component.scss',
})
export class AccountCommentsEditorComponent extends DynamicComponentBase implements AfterViewInit {

    // account for which the comments are being edited (in combination with field)
    readonly account = input.required<AccountData>();

    // segment data
    readonly segment = input.required<SegmentWithPermissions | undefined>();

    // field for which the comments are being edited
    readonly field = input.required<string>();

    // collection view used to generate update notifications when the comments are modified
    readonly collectionView = input<CollectionView<AccountData>>();

    // autofocus (or the opposite!) for the textarea 'new comment'
    readonly autofocus = input<boolean | undefined>(undefined);

    // event emitter when the comments have been updated (with the updated account data)
    readonly onCommentsUpdated = output<AccountData>();

    // internal flat list of comments for the account and field
    protected readonly comments = signal<CellComment[]>([])

    protected AppFormat = AppFormat;
    private sanitizer = inject(DomSanitizer);
    private readonly authService = inject(AuthService);

    private readonly el = inject(ElementRef);
    private readonly newCommentTextArea = viewChild.required<ElementRef<HTMLTextAreaElement>>('newComment');

    private readonly accessControlSvc = inject(AccessControlService);

    protected canAddComments = signal(false);

    constructor() {
        super();
        effect(() => this.loadCommentsForEditor());
        effect(() => this.updatePermissions());
    }

    override ngAfterViewInit() {
        super.ngAfterViewInit();
        this.adjustFocus();
    }

    private updatePermissions() {
        const account = this.account();
        const segment = this.segment();
        this.canAddComments.set(!!segment && this.accessControlSvc.canAddComment(segment, account));
    }

    private loadCommentsForEditor() {
        const account = this.account();
        if (!Array.isArray(account.metadataComments)) {
            account.metadataComments = [];
        }
        const commentsForAttr = CheckUtils.getCommentsForAttribute(account, this.field());
        this.comments.set(commentsForAttr);
    }

    private addComment(comment: CellComment) {
        this.comments.update(comments => [...comments, comment]);
        (this.account().metadataComments as Array<any>)?.push(comment);
        this.notifyCommentsUpdated();
    }

    private updateComment(comment: EditableComment) {
        this.notifyCommentsUpdated();
    }

    private deleteComment(comment: CellComment) {
        this.comments.update(comments => comments.filter(c => c !== comment));
        const accountComments = this.account().metadataComments as CellComment[];
        const index = accountComments.findIndex(c => c.id === comment.id);
        if (index >= 0) {
            accountComments.splice(index, 1);
            this.onCommentsUpdated.emit(this.account());
        } else {
            console.error('Comment not found in account comments', comment);
        }
        this.notifyCommentsUpdated();
    }

    private notifyCommentsUpdated() {
        const event = new AccountDataNotifyCollectionChangedEventArgs(NotifyCollectionChangedAction.Change, this.account(), "metadataComments");
        this.collectionView()?.onCollectionChanged(event);
        this.onCommentsUpdated.emit(this.account());
    }

    protected renderMarkdown(markdown: string): string | null {
        const html = MarkdownRendererComponent.syncParseAndPurify(markdown);
        return this.sanitizer.sanitize(SecurityContext.HTML, html);
    }

    protected toggleEditMode(comment: EditableComment) {
        comment.editMode = !comment.editMode;
        this.adjustFocus();
    }

    protected async deleteCommentPopup(comment: CellComment) {
        const btn = await Alerts.alertPopup({
            owner: this.el.nativeElement,
            body: 'Are you sure you want to delete this comment?',
            buttons: [DialogButton.Yes, DialogButton.No],
        });

        if (btn === DialogButton.Yes) {
            this.deleteComment(comment);
        }

        this.adjustFocus();
    }

    protected saveEditedText(commentContainer: HTMLDivElement, comment: EditableComment) {
        const textArea = commentContainer.querySelector('textarea') as HTMLTextAreaElement;
        comment.content = textArea.value;
        comment.editMode = false;
        comment.createdAt = new Date();
        comment.createdBy = this.getCreatedBy();
        this.adjustFocus();
        this.updateComment(comment);
    }

    private getCreatedBy() {
        return this.authService.username ?? 'unknown';
    }

    protected onAppendComment(textArea: HTMLTextAreaElement) {
        const newComment: CellComment = {
            id: StringUtils.newUuid4(),
            field: this.field(),
            createdBy: this.authService.username ?? 'unknown',
            createdAt: new Date(),
            content: textArea.value
        };
        textArea.value = '';
        textArea.style.height = '20px';
        this.addComment(newComment);
    }

    protected adjustTextareaHeight(textArea: HTMLTextAreaElement) {
        const newLineCount = textArea.value.split('\n').length;
        const newHeight = Math.max(newLineCount * 20, 20);
        textArea.style.height = newHeight + 'px';
    }

    // move the focus to the component to prevent it from automatically
    // being closed when it's in a Wijmo popup with hide-on-blur
    private adjustFocus() {
        if (this.autofocus()) {
            const textArea = this.newCommentTextArea()!.nativeElement;
            textArea.focus();
        }
    }

    protected onCommentKeyDown(event: KeyboardEvent) {
        if (event.key === 'Enter' && event.ctrlKey) {
            event.preventDefault();
            this.onAppendComment(this.newCommentTextArea()!.nativeElement);
        }
    }

    protected canDeleteComment(comment: CellComment) {
        const segment = this.segment();
        if (!segment) return false;
        return this.accessControlSvc.canDeleteComment(segment, this.account(), comment);
    }

    protected canEditComment(comment: CellComment) {
        return this.canDeleteComment(comment);
    }
}
